import { Box, Paper, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import SystemBanner from "../../../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { earliestDateInBlob, SHIFTS_DATE_COLUMN } from "../../loadOrder";
import { type LoadRecurringResult, LoadResultDisplay, type LoadShiftsResult } from "../LoadResultDisplay";
import { NurseLoadSideEffects } from "../NurseLoadSideEffects";
import type { LoadNursesResults } from "../NursesSummary";
import { PreviewCard } from "../PreviewCard";
import { RosterLoadResults } from "../RosterLoadResults";
import { type DataLoadFile, fileTypeOf } from "./consts";

type Props = {
	file: DataLoadFile;
	index: number;
	departmentId: string | undefined;
	timezone: string | undefined;
	isNextInOrder: boolean;
	isLoading: boolean;
	/**
	 * True while the sequential load chain is running. It iterates a snapshot taken before the run, so
	 * removing a file mid-chain would still load it while its result and any error were discarded
	 * against an id no longer in state.
	 */
	locked: boolean;
	onSkipChange: (skip: boolean) => void;
	onFilteredBlobChange: (blob: Blob) => void;
	onEarliestDateChange: (date: DateTime | null) => void;
	onUpload: () => void;
	onRemove: () => void;
	onRetry: () => void;
	isRetrying: boolean;
};

export function DataLoadResultCard({
	file,
	index,
	departmentId,
	timezone,
	isNextInOrder,
	isLoading,
	locked,
	onSkipChange,
	onFilteredBlobChange,
	onEarliestDateChange,
	onUpload,
	onRemove,
	onRetry,
	isRetrying,
}: Props) {
	const { t } = useTranslation();
	const testId = `data-load-result-${file.id}`;
	// `buildOrderedFiles` drops files with no kind and Run is gated on there being none, so this is
	// unreachable. It stays a hard exit rather than a default: guessing a kind here would render the
	// wrong preview and hand the file to the wrong loader.
	if (!file.kind) return null;

	const heading = t("onboarding_load.result_heading", {
		index: index + 1,
		kind: t(`onboarding_load.kind.${file.kind}`),
		action: file.kind === "roster" ? ` (${t(`onboarding_load.roster_action.${file.rosterAction}`)})` : "",
		filename: file.file.name,
		status: file.loadResult
			? t("onboarding_load.status.uploaded")
			: file.skip
				? t("onboarding_load.status.skipped")
				: file.loadError
					? t("onboarding_load.status.load_failed")
					: "",
	});

	if (file.transformError) {
		return (
			<SystemBanner
				tone="error"
				dataTestId={`${testId}-transform-error`}
				title={t("onboarding_load.prepare_failed_title", { filename: file.file.name })}
				description={file.transformError}
				action={
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
						<SystemButton
							buttonVariant="secondary"
							size="small"
							onClick={onRetry}
							disabled={locked || isRetrying}
							loading={isRetrying}
							data-testid={`${testId}-retry`}
						>
							{t("onboarding_load.retry")}
						</SystemButton>
						<SystemButton buttonVariant="secondary" size="small" onClick={() => onSkipChange(true)} disabled={locked} data-testid={`${testId}-skip-failed`}>
							{t("onboarding_load.skip")}
						</SystemButton>
						<SystemButton buttonVariant="borderless" size="small" onClick={onRemove} disabled={locked} data-testid={`${testId}-remove`}>
							{t("onboarding_load.remove")}
						</SystemButton>
					</Box>
				}
			/>
		);
	}

	const uploadLabel = file.loadResult
		? t("onboarding_load.uploaded")
		: isLoading
			? t("onboarding_load.uploading")
			: isNextInOrder
				? t("onboarding_load.upload")
				: t("onboarding_load.waiting_turn");
	const uploadDisabled = locked || isLoading || file.loadResult !== null || file.skip || !isNextInOrder;

	return (
		<Box data-testid={testId}>
			<Typography variant="subtitle2" sx={{ mb: 0.5, color: "text.secondary" }}>
				{heading}
			</Typography>

			{file.loadError && (
				<SystemBanner
					tone="error"
					title={t("onboarding_load.load_failed_title")}
					description={t("onboarding_load.load_failed_description", { reason: file.loadError })}
					sx={{ mb: 1 }}
					dataTestId={`${testId}-load-error`}
				/>
			)}

			{file.mode === "transform" && file.transformedData ? (
				<PreviewCard
					fileName={file.file.name}
					fileType={fileTypeOf(file.kind)}
					transformedData={file.transformedData}
					dataTestIdPrefix={`${testId}-preview`}
					failedTabs={file.failedTabs}
					skip={file.skip}
					onSkipChange={locked ? () => undefined : onSkipChange}
					onCancel={locked ? () => undefined : onRemove}
					onFilteredBlobChange={(blob) => {
						onFilteredBlobChange(blob);
						// Follows the filtered payload: dropping a file's earliest rows raises its earliest
						// date, which can move it past another file in the load order.
						if (file.kind === "shifts") void earliestDateInBlob(blob, SHIFTS_DATE_COLUMN).then(onEarliestDateChange);
					}}
					uploadButtons={[{ label: uploadLabel, onClick: onUpload, disabled: uploadDisabled, loading: isLoading }]}
				/>
			) : (
				/* No preview for an as-is upload: a PreviewCard implies the FILTERED blob is what gets
				   sent, which would quietly turn "as-is" into a transform. */
				<Paper variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
					<Box sx={{ flex: 1, minWidth: 200 }}>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{file.file.name}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{t("onboarding_load.as_is_description")}
						</Typography>
					</Box>
					<SystemSwitch
						checked={file.skip}
						onChange={(e) => onSkipChange(e.target.checked)}
						disabled={locked}
						label={t("onboarding_load.skip_on_upload")}
						dataTestId={`${testId}-skip`}
					/>
					<SystemButton onClick={onUpload} disabled={uploadDisabled} loading={isLoading} data-testid={`${testId}-upload`}>
						{uploadLabel}
					</SystemButton>
					<SystemButton buttonVariant="secondary" onClick={onRemove} disabled={locked} data-testid={`${testId}-remove`}>
						{t("onboarding_load.remove")}
					</SystemButton>
				</Paper>
			)}

			{file.loadResult && (
				<Box sx={{ mt: 2 }}>
					{file.kind === "roster" ? (
						<>
							{/* Side effects on OTHER nurses and on the department, which NursesSummary does not report. */}
							<NurseLoadSideEffects results={file.loadResult as LoadNursesResults} dataTestId={testId} />
							<RosterLoadResults results={file.loadResult as LoadNursesResults} timezone={timezone} dataTestId={testId} />
						</>
					) : (
						<LoadResultDisplay result={file.loadResult as LoadShiftsResult | LoadRecurringResult} departmentId={departmentId} timezone={timezone} />
					)}
				</Box>
			)}
		</Box>
	);
}
